import PropTypes from "prop-types";

import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import SelectedStateIndicator from "@/components/SelectedStateIndicator";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import ALPHABET from "@/view/constants/alphabet";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import {
  choiceLetterCss,
  choiceTextCss,
  inputCss,
  multipleChoiceCss,
  multipleChoiceContainerCss,
  interactiveMultipleChoiceLabelCss,
  containerCss,
} from "./InteractiveMultipleChoice.style";

const propTypes = {
  /**
   * The position of the option
   */
  choiceIndex: PropTypes.number.isRequired,
  choiceText: PropTypes.string.isRequired,
  groupName: PropTypes.string,
  handleSelect: PropTypes.func,
  isSelected: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  /**
   * The content id of the option
   */
  optionId: PropTypes.string,
  choiceType: PropTypes.string,
  textId: PropTypes.string,
  userSelectedCorrectChoice: PropTypes.bool,
  selectedChoiceContentId: PropTypes.string,
};

const defaultProps = {
  isSelected: false,
};

const InteractiveMultipleChoice = ({
  choiceIndex,
  choiceText,
  groupName,
  handleSelect,
  isSelected,
  isSubmitted = false,
  optionId,
  choiceType,
  userSelectedCorrectChoice = false,
  selectedChoiceContentId = "",
}) => {
  const inputId = `${choiceType}-${choiceIndex}`;

  const { isReadAloudModeActive } = useReadAloudMode();

  const [readAloudQuizzesVariation] =
    useGetUserExperimentSegment("read_aloud_quizzes");

  // Screen readers were not reading the option text correctly, so instead we're using an `aria-label` on the input and hiding the option text with `aria-hidden`.
  const userHasSelectedQuestion = () => selectedChoiceContentId === optionId;

  const renderLabel = () => {
    if (
      !isSubmitted ||
      (!userSelectedCorrectChoice && !userHasSelectedQuestion())
    )
      return null;

    const labelProps = {
      labelColor: !userSelectedCorrectChoice ? "primary" : "success",
      status: !userSelectedCorrectChoice ? "primary" : "correct",
      text: !userSelectedCorrectChoice ? "Your answer" : "Correct answer",
    };

    return (
      <ActivityChoiceIndicator
        __cssFor={interactiveMultipleChoiceLabelCss}
        labelColor={labelProps.labelColor}
        status={labelProps.status}
        text={labelProps.text}
      />
    );
  };

  const renderProperAriaLabel = () => {
    const baseText = `${ALPHABET[choiceIndex].toUpperCase() + " " + choiceText}`;
    if (
      !isSubmitted ||
      (!userSelectedCorrectChoice && !userHasSelectedQuestion())
    )
      return baseText;

    return !userSelectedCorrectChoice
      ? `Your answer: ${baseText}`
      : `Correct answer: ${baseText}`;
  };

  const isReadAloudButtonActive =
    isReadAloudModeActive && readAloudQuizzesVariation === "on";

  return (
    <div css={containerCss}>
      <label
        htmlFor={inputId}
        css={multipleChoiceContainerCss(
          {
            userSelectedCorrectChoice,
            isSubmitted,
            userHasSelectedQuestion: userHasSelectedQuestion(),
          },
          isReadAloudButtonActive,
        )}
      >
        <input
          aria-label={renderProperAriaLabel()}
          aria-checked={isSelected}
          css={inputCss}
          id={inputId}
          name={groupName}
          onChange={() => handleSelect(optionId)}
          type="radio"
          value={choiceIndex}
          checked={isSelected}
          disabled={isSubmitted}
        />
        <div aria-hidden="true" css={[multipleChoiceCss]}>
          {renderLabel()}
          <SelectedStateIndicator
            shouldDisplay={isSubmitted ? false : isSelected}
          />
          <span css={choiceLetterCss}>
            {ALPHABET[choiceIndex].toUpperCase() || ""}
          </span>
          <span css={choiceTextCss}>{choiceText}</span>
        </div>
      </label>
    </div>
  );
};

InteractiveMultipleChoice.propTypes = propTypes;
InteractiveMultipleChoice.defaultProps = defaultProps;

export default InteractiveMultipleChoice;
